import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import {
  Bell,
  Save,
  RefreshCw,
  Send,
  Mail,
  MessageSquare,
  Smartphone,
  AlertCircle,
  CheckCircle,
  Trash2,
  Wrench,
} from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Admin Portal -> Settings -> Notifications
 *
 * Who gets told about platform events, by email, Slack, Microsoft Teams and
 * WhatsApp (via Twilio). The first section covers trial signups (someone
 * signs up, their practice is ready, provisioning fails); further
 * notification types slot in as new sections. Secrets (webhook URLs, Twilio
 * token) are write-only: the server only reports whether each is configured.
 */

const EMPTY = {
  notifyEmails: '',
  notifyOnSignupStarted: true,
  notifyOnSignupCompleted: true,
  notifyOnSignupFailed: true,
  whatsappEnabled: false,
  twilioAccountSid: '',
  twilioWhatsappFrom: '',
  twilioSmsFrom: '',
  whatsappNumbers: '',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function splitList(value) {
  return String(value || '')
    .split(/[\n,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

const STATUS_LABELS = {
  sent: { text: 'Sent', className: 'text-green-700 bg-green-50 border-green-200' },
  partial: { text: 'Partly sent', className: 'text-amber-700 bg-amber-50 border-amber-200' },
  failed: { text: 'Failed', className: 'text-red-700 bg-red-50 border-red-200' },
  not_configured: { text: 'Not configured', className: 'text-gray-600 bg-gray-50 border-gray-200' },
  skipped: { text: 'Skipped', className: 'text-gray-600 bg-gray-50 border-gray-200' },
};

const NotificationsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState({
    slackWebhookConfigured: false,
    teamsWebhookConfigured: false,
    twilioAuthTokenConfigured: false,
    encryptionConfigured: true,
    updatedAt: null,
    updatedBy: '',
  });
  // Secret fields are only sent when touched: '' clears, a value replaces.
  const [secrets, setSecrets] = useState({ slackWebhookUrl: undefined, teamsWebhookUrl: undefined, twilioAuthToken: undefined });
  const [testResults, setTestResults] = useState(null);
  const [repairResult, setRepairResult] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const applySettings = (settings, encryptionConfigured) => {
    setForm({
      notifyEmails: (settings.notifyEmails || []).join('\n'),
      notifyOnSignupStarted: settings.notifyOnSignupStarted !== false,
      notifyOnSignupCompleted: settings.notifyOnSignupCompleted !== false,
      notifyOnSignupFailed: settings.notifyOnSignupFailed !== false,
      whatsappEnabled: settings.whatsappEnabled === true,
      twilioAccountSid: settings.twilioAccountSid || '',
      twilioWhatsappFrom: settings.twilioWhatsappFrom || '',
      twilioSmsFrom: settings.twilioSmsFrom || '',
      whatsappNumbers: (settings.whatsappNumbers || []).join('\n'),
    });
    setEvents(settings.events || []);
    setStatus((prev) => ({
      ...prev,
      slackWebhookConfigured: Boolean(settings.slackWebhookConfigured),
      teamsWebhookConfigured: Boolean(settings.teamsWebhookConfigured),
      twilioAuthTokenConfigured: Boolean(settings.twilioAuthTokenConfigured),
      encryptionConfigured: encryptionConfigured !== false,
      updatedAt: settings.updatedAt || null,
      updatedBy: settings.updatedBy || '',
    }));
    setSecrets({ slackWebhookUrl: undefined, teamsWebhookUrl: undefined, twilioAuthToken: undefined });
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getSignupNotificationSettings();
      if (response.data?.settings) applySettings(response.data.settings, response.data.encryptionConfigured);
    } catch (err) {
      toast.error('Failed to load signup notification settings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const change = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const changeSecret = (field, value) => setSecrets((prev) => ({ ...prev, [field]: value }));

  const emails = splitList(form.notifyEmails);
  const badEmails = emails.filter((e) => !EMAIL_RE.test(e));
  const numbers = splitList(form.whatsappNumbers);
  const anyChannel =
    emails.length > 0 ||
    status.slackWebhookConfigured || (secrets.slackWebhookUrl || '').trim() ||
    status.teamsWebhookConfigured || (secrets.teamsWebhookUrl || '').trim() ||
    (form.whatsappEnabled && numbers.length > 0);

  const handleSave = async () => {
    if (badEmails.length) {
      toast.error(`Not a valid email address: ${badEmails.join(', ')}`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        notifyEmails: emails,
        notifyOnSignupStarted: form.notifyOnSignupStarted,
        notifyOnSignupCompleted: form.notifyOnSignupCompleted,
        notifyOnSignupFailed: form.notifyOnSignupFailed,
        whatsappEnabled: form.whatsappEnabled,
        twilioAccountSid: form.twilioAccountSid,
        twilioWhatsappFrom: form.twilioWhatsappFrom,
        twilioSmsFrom: form.twilioSmsFrom,
        whatsappNumbers: numbers,
      };
      for (const key of ['slackWebhookUrl', 'teamsWebhookUrl', 'twilioAuthToken']) {
        if (secrets[key] !== undefined) payload[key] = secrets[key];
      }
      const response = await adminApi.updateSignupNotificationSettings(payload);
      if (response.data?.settings) applySettings(response.data.settings, status.encryptionConfigured);
      toast.success('Signup notification settings saved');
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to save settings';
      toast.error(message);
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (type) => {
    setTesting(true);
    setTestResults(null);
    try {
      const response = await adminApi.testSignupNotification(type);
      setTestResults(response.data?.results || null);
      toast.success('Test notification sent to every configured channel');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Test notification failed');
    } finally {
      setTesting(false);
    }
  };

  const handleRepair = async () => {
    setRepairing(true);
    setRepairResult(null);
    try {
      const response = await adminApi.backfillSignupContacts();
      setRepairResult(response.data?.data || null);
      toast.success('Tenant contact details repaired');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Repair failed');
    } finally {
      setRepairing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const renderSecretField = ({ label, field, configured, placeholder, help }) => {
    const touched = secrets[field] !== undefined;
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <div className="flex gap-2">
          <input
            type="password"
            autoComplete="off"
            value={touched ? secrets[field] : ''}
            onChange={(e) => changeSecret(field, e.target.value)}
            placeholder={configured && !touched ? 'Configured - paste a new value to replace' : placeholder}
            className="input flex-1"
          />
          {(configured || touched) && (
            <button
              type="button"
              onClick={() => changeSecret(field, '')}
              className="btn-secondary"
              title="Remove"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {touched && secrets[field] === '' ? 'Will be removed when you save. ' : ''}
          {help}
        </p>
      </div>
    );
  };

  const renderToggle = ({ field, label, description }) => (
    <label className="flex items-start gap-3 cursor-pointer">
      <input
        type="checkbox"
        checked={Boolean(form[field])}
        onChange={(e) => change(field, e.target.checked)}
        className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
      />
      <span>
        <span className="block text-sm font-medium text-gray-900">{label}</span>
        {description && <span className="block text-xs text-gray-500">{description}</span>}
      </span>
    </label>
  );

  const eventMeta = (type) => events.find((e) => e.type === type) || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bell className="w-6 h-6" />
            Notifications
          </h1>
          <p className="text-gray-500 mt-1">
            Who hears about what happens on the platform, and how. Starts with trial signups; more
            notification types will be added here.
          </p>
        </div>
        <button onClick={handleSave} disabled={saving} className="btn-primary">
          {saving ? <RefreshCw className="w-5 h-5 mr-2 animate-spin" /> : <Save className="w-5 h-5 mr-2" />}
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {anyChannel ? (
        <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-4">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-green-800">
            <p className="font-medium">Notifications are on</p>
            <p className="mt-1">
              {emails.length > 0 && `${emails.length} email address${emails.length === 1 ? '' : 'es'}`}
              {emails.length > 0 && (status.slackWebhookConfigured || status.teamsWebhookConfigured || form.whatsappEnabled) && ', '}
              {status.slackWebhookConfigured && 'Slack'}
              {status.slackWebhookConfigured && (status.teamsWebhookConfigured || form.whatsappEnabled) && ', '}
              {status.teamsWebhookConfigured && 'Microsoft Teams'}
              {status.teamsWebhookConfigured && form.whatsappEnabled && ', '}
              {form.whatsappEnabled && numbers.length > 0 && `WhatsApp to ${numbers.length} number${numbers.length === 1 ? '' : 's'}`}
              .
            </p>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium">Nobody is being notified</p>
            <p className="mt-1">
              Add at least one email address below. New signups are still saved to the Leads and
              Contacts lists, but no one is told they happened.
            </p>
          </div>
        </div>
      )}

      {!status.encryptionConfigured && (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-800">
            <p className="font-medium">ENCRYPTION_KEY is not set on the API worker</p>
            <p className="mt-1">
              Webhook URLs and the Twilio token are stored encrypted and cannot be saved until it is.
              Email notifications still work.
            </p>
          </div>
        </div>
      )}

      {/* Events */}
      <div className="card p-6 space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Trial signups</h2>
        <p className="text-sm text-gray-600">
          Every new signup also lands in Sales &rarr; Leads and Marketing &rarr; Contacts.
        </p>
        <div className="space-y-3">
          {renderToggle({ field: "notifyOnSignupStarted", label: eventMeta('signup_started').label || 'New trial signup', description: eventMeta('signup_started').description || 'Someone submitted the signup form and asked for a trial.' })}
          {renderToggle({ field: "notifyOnSignupCompleted", label: eventMeta('signup_completed').label || 'Trial account ready', description: eventMeta('signup_completed').description || 'Provisioning finished and the new practice can log in.' })}
          {renderToggle({ field: "notifyOnSignupFailed", label: eventMeta('signup_failed').label || 'Signup failed', description: eventMeta('signup_failed').description || 'Provisioning failed and needs a human.' })}
        </div>
      </div>

      {/* Email */}
      <div className="card p-6 space-y-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Mail className="w-5 h-5 text-gray-500" />
          Email
        </h2>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Send to</label>
          <textarea
            rows={3}
            value={form.notifyEmails}
            onChange={(e) => change('notifyEmails', e.target.value)}
            placeholder={'gavin@dolmanbateman.com.au\nadmin@thetaxaccountant.com.au'}
            className="input font-mono text-sm"
          />
          <p className="text-xs text-gray-500 mt-1">One address per line (commas work too).</p>
          {badEmails.length > 0 && (
            <p className="text-xs text-red-600 mt-1">Not a valid email address: {badEmails.join(', ')}</p>
          )}
        </div>
      </div>

      {/* Slack + Teams */}
      <div className="card p-6 space-y-6">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-gray-500" />
          Slack and Microsoft Teams
        </h2>
        {renderSecretField({
          label: 'Slack Incoming Webhook URL',
          field: 'slackWebhookUrl',
          configured: status.slackWebhookConfigured,
          placeholder: 'https://hooks.slack.com/services/...',
          help: 'In Slack: Apps -> Incoming Webhooks -> Add to Slack, pick the channel, copy the URL. Stored encrypted.',
        })}
        {renderSecretField({
          label: 'Microsoft Teams webhook URL',
          field: 'teamsWebhookUrl',
          configured: status.teamsWebhookConfigured,
          placeholder: 'https://....logic.azure.com/...',
          help: "In Teams: channel -> Workflows -> 'Post to a channel when a webhook request is received'. Stored encrypted.",
        })}
      </div>

      {/* WhatsApp */}
      <div className="card p-6 space-y-6">
        <div className="flex items-start justify-between gap-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-gray-500" />
            WhatsApp (via Twilio)
          </h2>
          {renderToggle({ field: "whatsappEnabled", label: "Enabled" })}
        </div>
        <p className="text-sm text-gray-600">
          WhatsApp has no plain webhook, so messages go through Twilio's WhatsApp API. Use the Twilio
          sandbox to try it (each recipient joins the sandbox once from their phone), or a WhatsApp
          Business sender for production.
        </p>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Twilio Account SID</label>
            <input
              type="text"
              value={form.twilioAccountSid}
              onChange={(e) => change('twilioAccountSid', e.target.value)}
              placeholder="AC..."
              className="input font-mono text-sm"
            />
          </div>
          {renderSecretField({ label: "Twilio Auth Token", field: "twilioAuthToken", configured: status.twilioAuthTokenConfigured, placeholder: "Auth token from the Twilio console", help: "Stored encrypted." })}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp sender number</label>
            <input
              type="text"
              value={form.twilioWhatsappFrom}
              onChange={(e) => change('twilioWhatsappFrom', e.target.value)}
              placeholder="+14155238886"
              className="input font-mono text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">The Twilio sandbox number, or your approved WhatsApp Business number.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">SMS sender number</label>
            <input
              type="text"
              value={form.twilioSmsFrom}
              onChange={(e) => change('twilioSmsFrom', e.target.value)}
              placeholder="+61480000000"
              className="input font-mono text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              A Twilio phone number that can send SMS. Used for admin sign-in codes (Settings &rarr; Admin Users &rarr; MFA) with the same Account SID and token.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Send to</label>
            <textarea
              rows={3}
              value={form.whatsappNumbers}
              onChange={(e) => change('whatsappNumbers', e.target.value)}
              placeholder={'0412 345 678\n+61 400 000 000'}
              className="input font-mono text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">One number per line. Australian mobiles can be typed as 04..., anything else needs a country code.</p>
          </div>
        </div>
      </div>

      {/* Test */}
      <div className="card p-6 space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Send a test</h2>
        <p className="text-sm text-gray-600">
          Save first, then send a sample to every configured channel. The sample is clearly marked
          [TEST] and uses made-up practice details.
        </p>
        <div className="flex flex-wrap gap-2">
          {['signup_started', 'signup_completed', 'signup_failed'].map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => handleTest(type)}
              disabled={testing}
              className="btn-secondary"
            >
              {testing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Test: {eventMeta(type).label || type}
            </button>
          ))}
        </div>
        {testResults && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(testResults).map(([channel, result]) => {
              const s = STATUS_LABELS[result] || STATUS_LABELS.skipped;
              return (
                <div key={channel} className={`rounded-lg border p-3 text-sm ${s.className}`}>
                  <div className="font-medium capitalize">{channel}</div>
                  <div>{s.text}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Repair */}
      <div className="card p-6 space-y-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Wrench className="w-5 h-5 text-gray-500" />
          Repair tenants missing contact details
        </h2>
        <p className="text-sm text-gray-600">
          Tenants created before this fix were provisioned without their owner's email, phone or
          ABN on the tenant record, and without a lead or contact. This copies those details from
          the original signup request and creates the missing lead and contact. Safe to run again.
        </p>
        <button type="button" onClick={handleRepair} disabled={repairing} className="btn-secondary">
          {repairing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Wrench className="w-4 h-4 mr-2" />}
          {repairing ? 'Repairing...' : 'Repair now'}
        </button>
        {repairResult && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-800 space-y-1">
            <div>Tenants checked: <strong>{repairResult.tenantsChecked}</strong></div>
            <div>Contact details copied: <strong>{repairResult.tenantsUpdated}</strong></div>
            <div>Leads / contacts synced: <strong>{repairResult.crmSynced}</strong></div>
            {repairResult.noSignupFound?.length > 0 && (
              <div className="text-amber-700">
                No signup request found (created another way, contact must be typed in by hand):{' '}
                {repairResult.noSignupFound.join(', ')}
              </div>
            )}
            {repairResult.errors?.length > 0 && (
              <div className="text-red-700">Errors: {repairResult.errors.join('; ')}</div>
            )}
          </div>
        )}
      </div>

      {status.updatedAt && (
        <p className="text-xs text-gray-500">
          Last saved {new Date(status.updatedAt).toLocaleString('en-AU')}{status.updatedBy ? ` by ${status.updatedBy}` : ''}.
        </p>
      )}
    </div>
  );
};

export default NotificationsPage;
